/**
 * Repository base for the agent PWA. Mirrors `apps/dashboard/src/lib/repositories/_base.ts`.
 *
 * Per the repo rule: this file is one of the two allowed to import
 * `firebase/firestore` in agent-pwa (the other is `lib/firebase.ts`).
 * Feature code must go through a typed repo built on top of `createRepo`.
 */

import {
  type CollectionReference,
  type DocumentData,
  type DocumentReference,
  type Query,
  type QueryConstraint,
  type Unsubscribe,
  type WithFieldValue,
  type PartialWithFieldValue,
  type SetOptions,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import type { z } from 'zod';

import { db } from '../firebase.js';
import { logger } from '../logger.js';
import { SchemaValidationFailedError } from '@kilat/shared';

export interface Repo<T> {
  get(id: string): Promise<T | null>;
  list(): Promise<T[]>;
  /**
   * Filtered read of a ROOT collection.
   *
   * Every other repo in this app is path-scoped (`agents/{id}/earnings`), so
   * the collection itself is the filter and `list()` suffices. Warm leads are
   * the first exception: `contactInquiries` is a root collection whose rows
   * belong to an agent by FIELD (`assignedAgentId`), and firestore.rules only
   * admits a query that says so. Mirrors `apps/find`'s repo base.
   */
  listWith(...constraints: QueryConstraint[]): Promise<T[]>;
  /**
   * Subscribe to a single doc. `onError` (optional, recommended) surfaces
   * Firestore-level listener errors (permission, network, quota) so the
   * caller can distinguish "doc missing" from "couldn't read" and avoid
   * the silent-loading dead-end per [[feedback-no-dead-ends]]. Without it a
   * denied/dropped read never fires `cb`, so a `useTrackedSnapshot` loading
   * flag stays `true` forever. Mirrors customer-pwa's repo base (PRs #65/#66).
   */
  snapshot(id: string, cb: (value: T | null) => void, onError?: (err: Error) => void): Unsubscribe;
  /**
   * Subscribe to the whole collection. Skips docs that fail schema
   * validation. `onError` (optional) — see `snapshot` for why.
   */
  listSnapshot(cb: (values: T[]) => void, onError?: (err: Error) => void): Unsubscribe;
  /**
   * Overwrite a doc. Validates the shape (throws on invalid) but PERSISTS the
   * caller's ORIGINAL object — never the Zod-parsed clone. Zod's parse turns a
   * `Timestamp` into a plain object with function-valued `toMillis`/`toDate`
   * that Firestore rejects as an unencodable field value.
   *
   * HAZARD (ADR-0027): because the original object is persisted unmodified,
   * `set()` no longer strips stray fields (the ADR-0026 self-protection is
   * gone). Never echo a raw repo READ object in — reads now carry the doc
   * `id`, so a stray post-parse `id` would be written. Pass an explicit
   * field patch.
   */
  set(id: string, value: T, options?: SetOptions): Promise<void>;
  /**
   * HAZARD (ADR-0026): reads now carry the doc `id`, and `update()` writes
   * the patch UNPARSED. Never echo a raw repo READ object here — pass an
   * explicit field patch, or a stray `id` would be persisted into the doc.
   */
  update(id: string, patch: PartialWithFieldValue<T>): Promise<void>;
}

export interface RepoOptions<S extends z.ZodTypeAny> {
  readonly path: string;
  readonly schema: S;
  readonly name?: string;
}

/**
 * See `apps/customer-pwa/src/lib/repositories/_base.ts` for the longer
 * rationale — Sprint 47 fix for the audit's type-inference findings.
 */
export function createRepo<S extends z.ZodTypeAny>(opts: RepoOptions<S>): Repo<z.output<S>> {
  type T = z.output<S>;
  const colRef = collection(db, opts.path) as CollectionReference<T & DocumentData>;

  const parseOrThrow = (raw: unknown): T => {
    const result = opts.schema.safeParse(raw);
    if (!result.success) {
      throw new SchemaValidationFailedError(opts.name ?? opts.path, result.error.flatten());
    }
    return result.data as T;
  };

  return {
    async get(id: string): Promise<T | null> {
      const snap = await getDoc(doc(colRef, id) as DocumentReference<T & DocumentData>);
      if (!snap.exists()) return null;
      // Attach the Firestore doc id AFTER the parse — never before. Zod
      // `z.object` STRIPS undeclared keys, so a pre-parse `{ id, ...data }`
      // silently dropped the id for any schema that didn't declare it (the
      // missing-id bug class). Re-attaching it as the last spread key
      // post-parse makes the id always survive, regardless of the schema —
      // the class is closed permanently. See ADR-0026.
      return { ...parseOrThrow(snap.data()), id };
    },
    async listWith(...constraints: QueryConstraint[]): Promise<T[]> {
      const snap = await getDocs(query(colRef as Query<T & DocumentData>, ...constraints));
      // id attached post-parse so the schema can never strip it (ADR-0026).
      return snap.docs.map((d) => ({ ...parseOrThrow(d.data()), id: d.id }));
    },
    async list(): Promise<T[]> {
      const snap = await getDocs(colRef as Query<T & DocumentData>);
      // id attached post-parse so it can never be stripped by the schema (ADR-0026).
      return snap.docs.map((d) => ({ ...parseOrThrow(d.data()), id: d.id }));
    },
    snapshot(
      id: string,
      cb: (value: T | null) => void,
      onError?: (err: Error) => void,
    ): Unsubscribe {
      return onSnapshot(
        doc(colRef, id) as DocumentReference<T & DocumentData>,
        (snap) => {
          if (!snap.exists()) {
            cb(null);
            return;
          }
          try {
            // id attached post-parse so it can never be stripped by the schema (ADR-0026).
            cb({ ...parseOrThrow(snap.data()), id });
          } catch {
            cb(null);
          }
        },
        (err) => {
          // Un-migrated callers pass no onError: log rather than silently swallow
          // the listener error (permission/network/quota). Keeps the agent-pwa
          // and customer-pwa repo bases identical (ADR-0026 follow-up).
          if (onError) {
            onError(err);
          } else {
            logger.warn('repo snapshot listener error (unhandled)', {
              path: opts.path,
              error: String(err),
            });
          }
        },
      );
    },
    listSnapshot(cb: (values: T[]) => void, onError?: (err: Error) => void): Unsubscribe {
      return onSnapshot(
        colRef as Query<T & DocumentData>,
        (snap) => {
          const out: T[] = [];
          for (const d of snap.docs) {
            try {
              // id attached post-parse so it can never be stripped by the schema (ADR-0026).
              out.push({ ...parseOrThrow(d.data()), id: d.id });
            } catch {
              // Skip individual bad docs rather than dropping the whole subscription.
            }
          }
          cb(out);
        },
        (err) => {
          // Un-migrated callers pass no onError: log rather than silently swallow
          // the listener error (permission/network/quota). Keeps the agent-pwa
          // and customer-pwa repo bases identical (ADR-0026 follow-up).
          if (onError) {
            onError(err);
          } else {
            logger.warn('repo snapshot listener error (unhandled)', {
              path: opts.path,
              error: String(err),
            });
          }
        },
      );
    },
    async set(id: string, value: T, options?: SetOptions): Promise<void> {
      // Validate the shape (throws on invalid) but PERSIST the caller's ORIGINAL
      // object — never the Zod-parsed clone. Zod's object parser copies a
      // Firestore `Timestamp`'s prototype methods (`toMillis`/`toDate`) onto a
      // plain object, which `setDoc` then rejects: "Unsupported field value: a
      // function (found in field …toMillis)". That silently broke every
      // client-side `.set()` writing a Timestamp field (promotions, vouchers,
      // tags, campaigns) — none were e2e-covered, so it went unnoticed until the
      // promotions-builder e2e. Writing the original object keeps real Timestamp
      // instances intact. Schemas use no `.transform()`/coercion (verified), so
      // the only thing not persisted is Zod defaults — and those are re-applied
      // on every read, so nothing is lost.
      parseOrThrow(value);
      const ref = doc(colRef, id) as DocumentReference<T & DocumentData>;
      await (options
        ? setDoc(ref, value as WithFieldValue<T & DocumentData>, options)
        : setDoc(ref, value as WithFieldValue<T & DocumentData>));
    },
    async update(id: string, patch: PartialWithFieldValue<T>): Promise<void> {
      await updateDoc(
        doc(colRef, id) as DocumentReference<T & DocumentData>,
        patch as DocumentData,
      );
    },
  };
}
